import React, { useRef } from "react";
import { Camera, X, ImagePlus } from "lucide-react";
import { Input } from "@/components/ui/input";

const compress = (file, maxDim = 1200, quality = 0.7) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > height && width > maxDim) { height = (height * maxDim) / width; width = maxDim; }
        else if (height > maxDim) { width = (width * maxDim) / height; height = maxDim; }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

export default function PhotoUpload({ photos = [], onChange }) {
  const inputRef = useRef(null);
  const camRef = useRef(null);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    const newPhotos = [];
    for (const f of files) {
      const data = await compress(f);
      newPhotos.push({ id: Math.random().toString(36).slice(2), data, caption: "" });
    }
    onChange([...(photos || []), ...newPhotos]);
    e.target.value = "";
  };

  const remove = (id) => onChange(photos.filter((p) => p.id !== id));
  const setCaption = (id, caption) => onChange(photos.map((p) => (p.id === id ? { ...p, caption } : p)));

  return (
    <div className="space-y-3">
      <input ref={inputRef} type="file" accept="image/*" multiple onChange={handleFiles} className="hidden" data-testid="photo-file-input" />
      <input ref={camRef} type="file" accept="image/*" capture="environment" onChange={handleFiles} className="hidden" data-testid="photo-camera-input" />
      <div className="grid grid-cols-2 gap-2">
        <button type="button" onClick={() => camRef.current?.click()} data-testid="take-photo-btn"
          className="h-24 rounded-xl border-2 border-dashed border-primary/50 bg-primary/5 hover:bg-primary/10 transition-colors flex flex-col items-center justify-center gap-1 text-primary">
          <Camera className="h-7 w-7" /><span className="text-sm font-semibold">Tirar foto</span>
        </button>
        <button type="button" onClick={() => inputRef.current?.click()} data-testid="add-photo-btn"
          className="h-24 rounded-xl border-2 border-dashed border-border hover:border-primary hover:bg-accent transition-colors flex flex-col items-center justify-center gap-1 text-muted-foreground">
          <ImagePlus className="h-7 w-7" /><span className="text-sm font-medium">Da galeria</span>
        </button>
      </div>

      {photos?.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {photos.map((p) => (
            <div key={p.id} className="relative group rounded-xl overflow-hidden border border-border" data-testid={`photo-item-${p.id}`}>
              <img src={p.data} alt="foto" className="w-full h-28 object-cover" />
              <button type="button" onClick={() => remove(p.id)} data-testid={`remove-photo-${p.id}`}
                className="absolute top-1 right-1 h-7 w-7 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-red-600 transition-colors">
                <X className="h-4 w-4" />
              </button>
              <Input value={p.caption} onChange={(e) => setCaption(p.id, e.target.value)} placeholder="Legenda (ex.: Antes / Depois)"
                className="rounded-none border-0 border-t text-xs h-8" data-testid={`caption-${p.id}`} />
              <div className="flex gap-1 p-1 border-t border-border">
                {["Antes", "Durante", "Depois"].map((c) => <button key={c} type="button" onClick={() => setCaption(p.id, c)} className={`flex-1 h-6 rounded text-[10px] font-medium ${p.caption === c ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`} data-testid={`caption-${c}-${p.id}`}>{c}</button>)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
